import { OpenAPIV2 } from 'openapi-types'
import {
  TEntityOverride,
  TEntityOverrideProp,
  TEntityOverridePropType,
  TEntityView,
} from 'src/types'
import overrides from './entities'

const defaultOverride: TEntityOverride = {
  global: {
    properties: {},
  },
  list: {
    properties: {},
  },
  edit: {
    properties: {},
  },
  create: {
    properties: {},
  },
}

const getPropertyRef = (
  property: OpenAPIV2.SchemaObject,
): string | undefined => {
  return property.$ref ?? property.allOf?.[0]?.$ref
}

export const getOverridenEntityProperty = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  E extends Record<string, any> = Record<string, any>,
>(
  entity: OpenAPIV2.SchemaObject,
  propertyKey: string,
  override: Partial<TEntityOverrideProp<TEntityView, E>> = {},
): TEntityOverrideProp<TEntityView, E> => {
  const property = entity.properties?.[propertyKey]

  return getOverridenProperty(property ?? {}, override, entity, propertyKey)
}

export const getOverridenProperty = <ParentObject extends Record<string, any>>(
  property:
    | OpenAPIV2.SchemaObject
    | OpenAPIV2.ItemsObject
    | OpenAPIV2.ReferenceObject = {},
  override: Partial<
    TEntityOverrideProp<TEntityView, ParentObject, keyof ParentObject>
  > = {},
  parent: OpenAPIV2.SchemaObject,
  propertyKey?: string,
): TEntityOverrideProp<TEntityView, ParentObject, keyof ParentObject> => {
  const $ref = property !== undefined ? getPropertyRef(property) : undefined

  let type =
    override.type ?? ('type' in property ? property.type : $ref?.split('/')[2])
  if (
    type === 'string' &&
    'format' in property &&
    property?.format === 'date-time'
  )
    type = 'date-time'

  const rawItems = 'items' in property ? property.items : undefined
  const rawProperties =
    'properties' in property ? property.properties : undefined

  return {
    ...property,
    defaultValue: override.defaultValue,
    required:
      typeof override.required === 'boolean'
        ? override.required
        : typeof propertyKey === 'string' &&
          parent.required?.includes(propertyKey),
    $ref,
    type:
      typeof type === 'string'
        ? (type as unknown as TEntityOverridePropType)
        : 'undefined',
    enum: override.enum,
    basedOn: override.basedOn ?? undefined,
    displayedName: override.displayedName ?? propertyKey,
    reference: override.reference,
    readOnly:
      override.readOnly ??
      ('readOnly' in property && property?.readOnly) ??
      false,
    hidden: override.hidden ?? false,
    validations: override.validations ?? [],
    displayedComponent: override.displayedComponent,
    // $ref'd items/properties (e.g. array items pointing at a shared object
    // definition) aren't inlined here, so there's nothing to merge yet —
    // forward the override as-is; it's applied once rendering resolves the
    // real schema, via getOverridenObjectProperties.
    items:
      rawItems !== undefined
        ? getOverridenProperty(
            rawItems,
            override.items ?? {},
            property as OpenAPIV2.SchemaObject,
          )
        : override.items,
    properties:
      rawProperties !== undefined
        ? getOverridenObjectProperties(
            property as OpenAPIV2.SchemaObject,
            override.properties,
          )
        : override.properties,
  }
}

/**
 * Same per-property resolution as getOverridenEntityProperty, applied to
 * every property of a schema at once. Only goes one level deep: object
 * definitions can reference each other cyclically (e.g. tree structures), so
 * deeper levels are resolved the same way but lazily, when rendering
 * actually reaches them.
 */
export const getOverridenObjectProperties = (
  objectDefinition: OpenAPIV2.SchemaObject,
  overrideProperties: Record<string, TEntityOverrideProp> = {},
): Record<string, TEntityOverrideProp> => {
  const keys = Object.keys({
    ...objectDefinition.properties,
    ...overrideProperties,
  })

  return keys.reduce<Record<string, TEntityOverrideProp>>((acc, key) => {
    acc[key] = getOverridenEntityProperty(
      objectDefinition,
      key,
      overrideProperties[key],
    )
    return acc
  }, {})
}

/**
 * Resolves a property that's $ref'd to a shared object definition. The
 * definition fills in fields the property itself doesn't carry (e.g.
 * `additionalProperties`); the property's own config wins where both have
 * one; `properties` is replaced with the actual merged fields.
 */
export const getOverridenReferencedProperty = (
  objectDefinition: OpenAPIV2.SchemaObject,
  property: TEntityOverrideProp,
): TEntityOverrideProp =>
  // the raw swagger `type` (string | string[]) is narrowed at render time
  ({
    ...objectDefinition,
    ...property,
    properties: getOverridenObjectProperties(
      objectDefinition,
      property.properties,
    ),
  } as TEntityOverrideProp)

const getOverridenEntity = (
  entityName: string,
  entity: OpenAPIV2.SchemaObject,
): TEntityOverride => {
  const { properties = {}, required = [], ...props } = entity
  const {
    global = defaultOverride.global,
    list = defaultOverride.list,
    edit = defaultOverride.edit,
    create = defaultOverride.edit,
    ...paramsProps
  } = overrides[entityName] ?? {}

  return {
    ...props,
    ...paramsProps,
    required,
    global: {
      ...global,
      properties: Object.keys(properties).reduce<TEntityOverrideProp>(
        (acc, propertyKey) => {
          acc[propertyKey] = getOverridenEntityProperty(
            entity,
            propertyKey,
            global.properties[propertyKey],
          )
          return acc
        },
        {},
      ),
    },
    list,
    edit,
    create,
  }
}

export default (
  entities: Record<string, OpenAPIV2.SchemaObject>,
): Record<string, TEntityOverride> => {
  const res = Object.keys(entities).reduce(
    (acc, entityKey) => ({
      ...acc,
      [entityKey]: getOverridenEntity(entityKey, entities[entityKey]),
    }),
    {},
  )
  return res
}
